import React, { useMemo, useRef } from 'react';
import { useInternationalization } from '@progress/kendo-react-intl';
import {
  useSearchContext,
  useDocumentsContext,
  useCurrentUserContext,
  useGlobalizationContext,
  useTranslation,
  useViewsContext,
  useGetView,
  useGetAllFields
} from '@cs-cms/state-management';
import { createCpsGridCellRender } from './cpsGridCellRender';
import { createRemoveFilterHandler } from './helpers/cpsGridFilterRemoval';
import { CpsGridView } from './CpsGridView';
import { DocumentPreviewProvider, useDocumentPreviewContext } from './documentPreview';
import {
  useCpsGridColumns,
  useCpsGridDocuments,
  useCpsGridLayout,
  useCpsGridQueryEffects,
  useCpsGridSelection
} from './hooks';

export const CpsGrid: React.FC = () => {
  return (
    <DocumentPreviewProvider>
      <CpsGridContent />
    </DocumentPreviewProvider>
  );
};

const CpsGridContent: React.FC = () => {
  const { previewDocument } = useDocumentPreviewContext();
  const gridRef = useRef<HTMLDivElement>(null);
  const { isLoading, data } = useSearchContext();
  const { openDocument, openDocumentLoading } = useDocumentsContext();
  const { selectedIndex } = useCurrentUserContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { selectedView } = useViewsContext();
  const { data: currentView, isLoading: currentViewLoading } = useGetView(selectedView, selectedLocale);
  const { data: allFields } = useGetAllFields(selectedLocale, selectedIndex?.id);
  const intl = useInternationalization();

  const columns = currentView?.fields;
  const selectedIndexId = selectedIndex?.id;
  const selectedViewId = selectedView;

  const { gridData, initialFacets, actionsFacets } = useCpsGridDocuments({
    selectedIndexId,
    selectedView: selectedViewId,
    translate
  });

  const {
    queryRequest,
    setQueryRequest,
    compositeFilterDescriptor,
    setCompositeFilterDescriptor,
    sort,
    sortChange,
    pageChange,
    pageSizeValue,
    rowsCount
  } = useCpsGridQueryEffects({
    columns,
    selectedView: selectedViewId,
    selectedIndexId,
    currentViewGlobalFilters: currentView?.globalFilters,
    allFields,
    translate,
    isLoading,
    totalCountFromData: data?.totalCount
  });

  const { columnChanges, gridKey, onColumnResize, onColumnReorder, resetColumnWidths } = useCpsGridColumns({
    columns,
    gridRef,
    selectedIndexId,
    selectedView: selectedViewId
  });

  const { gridDimensions, noRecordsOverlayStyle } = useCpsGridLayout({
    gridRef,
    gridDataLength: gridData.length,
    selectedIndexId,
    columns,
    columnChanges,
    layoutRevision: previewDocument ? `${previewDocument.contentType}:${previewDocument.documentId}` : ''
  });

  const { select, onFilterActionsMount, onSelectionChange, onHeaderSelectionChange, handleContextMenu } =
    useCpsGridSelection({
      gridData,
      selectedIndexId,
      selectedView: selectedViewId
    });

  const cellRender = useMemo(
    () =>
      createCpsGridCellRender({
        columns,
        intl,
        openDocument
      }),
    [columns, intl, openDocument]
  );

  const removeFilter = useMemo(
    () =>
      createRemoveFilterHandler({
        queryRequest,
        setQueryRequest,
        compositeFilterDescriptor,
        setCompositeFilterDescriptor
      }),
    [queryRequest, setQueryRequest, compositeFilterDescriptor, setCompositeFilterDescriptor]
  );

  return (
    <CpsGridView
      gridRef={gridRef}
      onContextMenu={handleContextMenu}
      isLoading={Boolean(isLoading)}
      openDocumentLoading={Boolean(openDocumentLoading)}
      currentViewLoading={currentViewLoading}
      gridKey={gridKey}
      gridDimensions={gridDimensions}
      noRecordsOverlayStyle={noRecordsOverlayStyle}
      sort={sort}
      sortChange={sortChange}
      compositeFilterDescriptor={compositeFilterDescriptor}
      setCompositeFilterDescriptor={setCompositeFilterDescriptor}
      onColumnReorder={onColumnReorder}
      onColumnResize={onColumnResize}
      queryRequest={queryRequest}
      pageSizeValue={pageSizeValue}
      rowsCount={rowsCount}
      pageChange={pageChange}
      gridData={gridData}
      select={select}
      onSelectionChange={onSelectionChange}
      onHeaderSelectionChange={onHeaderSelectionChange}
      cellRender={cellRender}
      columnChanges={columnChanges}
      columns={columns}
      resetColumnWidths={resetColumnWidths}
      initialFacets={initialFacets}
      facets={data?.facets}
      actionsFacets={actionsFacets}
      onFilterActionsMount={onFilterActionsMount}
      onRemoveFilter={removeFilter}
      translate={translate}
    />
  );
};
